import * as R from 'remeda';
import { isMobile } from 'react-device-detect';
import { push, replace } from 'redux-first-history';
import { combineEpics } from 'redux-observable';
import { EMPTY, from, fromEvent, merge, of } from 'rxjs';
import {
  catchError,
  filter,
  first,
  map,
  mapTo,
  mergeMap,
  pluck,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { getType } from 'typesafe-actions';
import { endpoints } from '@rossum/api-client';
import { apiUrl, isEmbedded } from '../../../constants/config';
import { CHANGES_SAVED_PATH } from '../../../containers/ChangesSaved/constants';
import { QUERY_KEY_DOMAIN_SETTINGS } from '../../../containers/Login/useDomainSettings';
import { isFeatureEnabled } from '../../../features/feature-enablement/featureEnablement';
import { errorHandler } from '../../../lib/api';
import { api } from '../../../lib/apiClient';
import { isUserLoggedIn, LocationWithReferrerState } from '../../../lib/auth';
import { isIframeEmbedded } from '../../../lib/embeddedSsoSession';
import { queryClient } from '../../../lib/queryClient';
import {
  clearPendingSsoProvider,
  getSsoSessionProviderId,
} from '../../../lib/ssoSessionProvider';
import { isNonEmptyArray } from '../../../lib/typeGuards';
import {
  asScalar,
  AUTO_LOGIN_SSO_PROVIDER_ID,
  getAnnotationId,
  getCurrentAnnotationId,
  getEditModeUrl,
  getIDFromString,
  isSplitPathname,
  parse,
  removeAutoLoginParams,
} from '../../../lib/url';
import { getAnnotationBacklink } from '../../../routes/useAnnotationBacklink';
import { timeSpent } from '../../../timers/timeSpent/timeSpent';
import { EmbeddedConfig } from '../../../types/ui';
import {
  annotationExpired,
  cancelAnnotation,
  cancelAnnotationFulfilled,
  confirmAnnotation,
  confirmAnnotationFulfilled,
  deleteAnnotation,
  deleteAnnotationFulfilled,
  displayAnnotation,
  exitAnnotation,
  postponeAnnotation,
  postponeAnnotationFulfilled,
  startAnnotationFulfilled,
} from '../annotation/actions';
import { pagesSelector } from '../annotation/selectors';
import { popAnnotationFromStack } from '../annotations/actions';
import { signOut } from '../auth/actions';
import { confirmEditModeFulfilled } from '../editMode/actions';
import { ANNOTATIONS_QUERY } from '../localStorage/actions';
import { userLabConfigSelector } from '../user/selectors';
import { isActionOf, locationChange, makeEpic } from '../utils';
import {
  cancelEditMode,
  enterLogin,
  enterValidation,
  fetchEmbeddedConfigFullfiled,
  leaveValidation,
  setApplicationTabVisibility,
  startEditMode,
} from './actions';

export const embeddedConfigEpic = makeEpic(
  (action$, state$, { authGetJSON$ }) =>
    action$.pipe(
      filter(isActionOf(enterValidation)),
      filter(isEmbedded),
      map(() => state$.value),
      pluck('router', 'location', 'pathname'),
      map(getCurrentAnnotationId),
      mergeMap(annotationId =>
        authGetJSON$<EmbeddedConfig>(
          `${apiUrl}/annotations/${annotationId}/embedded/config`
        ).pipe(
          map(config => fetchEmbeddedConfigFullfiled(config)),
          catchError(errorHandler)
        )
      )
    )
);

const isMovingToSplitRoute = (
  pathname: string,
  annotationId: number | null | undefined
) =>
  isSplitPathname(pathname) &&
  getCurrentAnnotationId(pathname) === annotationId;

export const embeddedRedirectEpic = makeEpic((action$, state$) =>
  merge(
    action$.pipe(
      filter(isActionOf(cancelAnnotationFulfilled)),
      filter(isEmbedded),
      map(() => state$.value.ui.embeddedConfig?.cancelUrl)
    ),
    action$.pipe(
      filter(isActionOf(confirmAnnotationFulfilled)),
      filter(isEmbedded),
      map(() => state$.value.ui.embeddedConfig?.returnUrl)
    ),
    action$.pipe(
      filter(isActionOf(postponeAnnotationFulfilled)),
      filter(isEmbedded),
      map(() => state$.value.ui.embeddedConfig?.postponeUrl)
    ),
    action$.pipe(
      filter(isActionOf(deleteAnnotationFulfilled)),
      filter(isEmbedded),
      map(() => state$.value.ui.embeddedConfig?.deleteUrl)
    ),
    action$.pipe(
      filter(isActionOf(confirmEditModeFulfilled)),
      filter(isEmbedded),
      map(result => result.payload?.results[0]?.annotation),
      map(getIDFromString),
      withLatestFrom(
        state$.pipe(
          pluck('router', 'location', 'pathname'),
          map(getCurrentAnnotationId)
        )
      ),
      filter(([id, currentAnnotationId]) => id !== currentAnnotationId),
      map(() => state$.value.ui.embeddedConfig?.returnUrl)
    ),
    // Handle leaving of read-only annotation in embedded mode (via the back arrow in the top-left corner)
    action$.pipe(
      filter(isActionOf(displayAnnotation)),
      filter(isEmbedded),
      switchMap(() =>
        action$.pipe(
          filter(isActionOf(leaveValidation)),
          // MAT-1958: `leaveValidation` also fires when the validation screen
          // unmounts on a navigation to `/split/:id`, which stays on the very
          // same annotation — ejecting the user out of the iframe there would
          // be wrong. Compared against `window.location.pathname` (not the
          // router state) for consistency with the unmount effect in
          // containers/DocumentValidation that triggers this.
          filter(
            () =>
              !isMovingToSplitRoute(
                window.location.pathname,
                state$.value.annotation.id
              )
          ),
          map(() => state$.value.ui.embeddedConfig?.cancelUrl)
        )
      )
    )
  ).pipe(
    switchMap(url => {
      if (url) {
        window.location.replace(url);
        return of({ type: 'REDIRECT_TO_THE_UNIVERSE' });
      }
      return of(replace(CHANGES_SAVED_PATH));
    })
  )
);

export const startEditModeWithHashEpic = makeEpic((_, state$) =>
  state$.pipe(
    take(1),
    pluck('router', 'location'),
    filter(location => Boolean(getCurrentAnnotationId(location.pathname))),
    map(location => parse(location.hash)['editMode']),
    filter(R.isTruthy),
    switchMap(() =>
      state$.pipe(
        map(state => pagesSelector(state)),
        startWith(pagesSelector(state$.value)),
        filter(isNonEmptyArray),
        filter(() => {
          const { suggestedEdit } = state$.value.annotation;
          if (!suggestedEdit) {
            // Nothing to wait for
            return true;
          }

          if (typeof suggestedEdit === 'string') {
            // Waiting until it's loaded (string represents its URL)
            return false;
          }

          // It's loaded
          return true;
        }),
        take(1),
        map(() => {
          if (state$.value.ui.readOnly) return replace({ hash: '' });

          const isSplitEnabled = isFeatureEnabled(
            'mat-1949-splitting-view',
            userLabConfigSelector(state$.value)
          );

          if (isSplitEnabled) {
            const id = getCurrentAnnotationId(
              state$.value.router.location.pathname
            );
            return id === undefined
              ? startEditMode()
              : push(getEditModeUrl(true, { id }));
          }

          return startEditMode();
        })
      )
    )
  )
);

export const annotationExpiredRedirectEpic = makeEpic(action$ =>
  action$.pipe(
    filter(isActionOf(annotationExpired)),
    map(() => {
      const defaultListLocation = getAnnotationBacklink();
      return push(isEmbedded() ? '/timeExpired' : defaultListLocation);
    })
  )
);

export const exitAnnotationEpic = makeEpic(action$ =>
  action$.pipe(
    filter(isActionOf(startAnnotationFulfilled)),
    map(({ meta: { url } }) => url),
    mergeMap(url =>
      action$.pipe(
        filter(isActionOf(exitAnnotation)),
        pluck('payload', 'id'),
        filter(id => getAnnotationId(url) === id),
        first(),
        takeUntil(
          merge(
            action$.pipe(
              filter(
                isActionOf([
                  postponeAnnotation,
                  deleteAnnotation,
                  popAnnotationFromStack,
                  confirmAnnotation,
                  displayAnnotation,
                  annotationExpired,
                ])
              )
            ),
            // MAT-1958: the same annotation can be started more than once per
            // lifetime now (`/document/5` -> `/split/5` -> `/document/5`
            // remounts the validation screen). Without this, every return trip
            // stacks another pending watcher on the same url and a single
            // `exitAnnotation` fans out into N `cancelAnnotation` calls.
            action$.pipe(
              filter(isActionOf(startAnnotationFulfilled)),
              filter(({ meta }) => meta.url === url)
            )
          )
        ),
        mapTo(cancelAnnotation(url))
      )
    )
  )
);

export const cancelEmbeddedAnnotationEpic = makeEpic(action$ =>
  action$.pipe(
    filter(isActionOf(startAnnotationFulfilled)),
    map(({ meta: { url } }) => url),
    filter(isEmbedded),
    switchMap(url =>
      action$.pipe(
        filter(isActionOf(leaveValidation)),
        // MAT-1958: the validation screen also unmounts (and dispatches
        // `leaveValidation`) when navigating to `/split/:id`, which is still
        // the same annotation — cancelling it there would release the lock
        // under the new screen. Compared against `window.location.pathname`
        // for consistency with the unmount effect that triggers this.
        filter(
          () =>
            !isMovingToSplitRoute(
              window.location.pathname,
              getAnnotationId(url)
            )
        ),
        first(),
        takeUntil(
          action$.pipe(
            filter(
              isActionOf([
                postponeAnnotation,
                deleteAnnotation,
                confirmAnnotation,
                displayAnnotation,
                annotationExpired,
              ])
            )
          )
        ),
        mapTo(cancelAnnotation(url))
      )
    )
  )
);

const enterLoginEpic = makeEpic((action$, state$) =>
  merge(
    action$.pipe(
      filter(isActionOf(locationChange)),
      filter(({ payload }) => payload.location.pathname === '/')
    ),
    action$.pipe(filter(isActionOf(enterLogin)))
  ).pipe(
    filter(() => !isEmbedded()),
    filter(() =>
      isUserLoggedIn(state$.value.router.location as LocationWithReferrerState)
    ),
    map(() => {
      const pathname = localStorage.getItem(ANNOTATIONS_QUERY)?.split('/')[1];
      return pathname || '/annotations';
    }),
    map(pathname => push(pathname))
  )
);

const enterLoginEmbeddedEpic = makeEpic(action$ =>
  action$.pipe(
    filter(isActionOf(enterLogin)),
    filter(() => isEmbedded()),
    // EF-2448: The user should normally never get into Login page
    // in the embedded mode. But if it happens, redirect them
    // to /timeExpired so that they can retry the whole process.
    mapTo(replace('/timeExpired'))
  )
);

const redirectToMobileScreenEpic = makeEpic(action$ =>
  action$.pipe(
    filter(isActionOf(locationChange)),
    first(),
    filter(() => isMobile),
    filter(() => !isEmbedded()),
    map(({ payload: { location } }) => {
      const { pathname, search, hash } = location;
      const redirectUrl =
        pathname === '/mobileIntroScreen'
          ? undefined
          : `${pathname}${search}${hash}`;

      return push('/mobileIntroScreen', { redirectUrl });
    })
  )
);

const removeDatapointPathEpic = makeEpic((action$, state$) =>
  action$.pipe(
    filter(isActionOf(startEditMode)),
    map(() => {
      const search = new URLSearchParams(state$.value.router.location.search);
      search.delete('datapointPath');

      return replace({
        pathname: state$.value.router.location.pathname,
        search: search.toString(),
        state: state$.value.router.location.state,
      });
    })
  )
);

// A standalone autologin deep link (e.g. /documents?...&auto_login_sso_provider_id=5)
// reaches an already-logged-in user with the param still in the URL. Gated on
// isUserLoggedIn so that for a logged-out user the param flows untouched to
// RequireAuth's referrer, where useAutoSsoLogin picks it up. When logged in:
//   - same provider as the session → reuse it, just strip the param;
//   - different (or no) recorded provider → log out and re-login via the link's
//     provider, but only if that provider actually exists (a non-existent id can
//     never resolve and would otherwise loop login↔logout); for an unknown id we
//     drop the param and keep the current session / regular login.
// In iframe mode providers are handled by the sessionStorage pin + namespaced
// token (MAT-1724), so we never force a switch — only strip.
export const reconcileAutoLoginProviderEpic = makeEpic((action$, state$) =>
  action$.pipe(
    filter(isActionOf(locationChange)),
    filter(({ payload: { location } }) =>
      location.search.includes(AUTO_LOGIN_SSO_PROVIDER_ID)
    ),
    filter(() =>
      isUserLoggedIn(state$.value.router.location as LocationWithReferrerState)
    ),
    mergeMap(({ payload: { location } }) => {
      const desired = asScalar(
        parse(location.search)[AUTO_LOGIN_SSO_PROVIDER_ID]
      );

      if (!desired) {
        return EMPTY;
      }

      const stripParam = replace({
        pathname: location.pathname,
        search: removeAutoLoginParams(location.search),
        hash: location.hash,
        state: location.state,
      });

      // Reuse the session (same provider) or iframe (pin handles providers).
      if (isIframeEmbedded() || getSsoSessionProviderId() === desired) {
        return of(stripParam);
      }

      // Switch only to a real provider. Validate against domainSettings (same
      // source as useDomainSettings); on an unknown id or a fetch failure, don't
      // log out — drop the bogus param and any stale pending.
      const { hostname } = new URL(window.location.origin);

      return from(
        queryClient.fetchQuery({
          queryKey: [QUERY_KEY_DOMAIN_SETTINGS, hostname],
          queryFn: () =>
            api.request(
              endpoints.internal.domainSettings({ domain: hostname })
            ),
        })
      ).pipe(
        map(({ ssoProviders }) => {
          const isKnownProvider = ssoProviders.some(
            provider => String(provider.id) === desired
          );

          if (isKnownProvider) {
            return signOut({ referrer: location });
          }

          clearPendingSsoProvider();
          return stripParam;
        }),
        catchError(() => of(stripParam))
      );
    })
  )
);

const cancelEditModeEpic = makeEpic((action$, state$) =>
  action$.pipe(
    filter(isActionOf([exitAnnotation, confirmEditModeFulfilled])),
    filter(() => state$.value.ui.editModeActive),
    map(action =>
      // Timer for the current annotation should be already stoped by confirmEditMode action
      // when confirmEditModeFulfilled is dispatched.
      cancelEditMode(
        action.type === getType(exitAnnotation) ? action.payload.id : undefined
      )
    )
  )
);

const setTabVisibilityEpic = () =>
  fromEvent(document, 'visibilitychange').pipe(
    map(() =>
      setApplicationTabVisibility(document.visibilityState === 'visible')
    )
  );

const setPageVisibilityEpic = makeEpic(action$ =>
  action$.pipe(
    filter(isActionOf(setApplicationTabVisibility)),
    pluck('payload'),
    tap(b => {
      timeSpent.pageVisibilityHasChanged(b);
    }),
    mapTo({ type: 'EMPTY_ACTION' })
  )
);

export default combineEpics(
  annotationExpiredRedirectEpic,
  cancelEditModeEpic,
  cancelEmbeddedAnnotationEpic,
  embeddedConfigEpic,
  embeddedRedirectEpic,
  enterLoginEpic,
  setPageVisibilityEpic,
  setTabVisibilityEpic,
  enterLoginEmbeddedEpic,
  exitAnnotationEpic,
  redirectToMobileScreenEpic,
  removeDatapointPathEpic,
  startEditModeWithHashEpic,
  reconcileAutoLoginProviderEpic
);
